import React, { useState, useEffect, useRef } from 'react';
import { 
  Phone,
  MessageSquare,
  Clock,
  Loader2,
  CheckCircle,
  Send,
  User,
  PhoneOff,
  Home,
  MapPin,
  DollarSign,
  Camera,
  FileText,
  Download,
  Upload,
  Star,
  Award,
  Hammer,
  Calendar,
  Image as ImageIcon,
  Monitor,
  MonitorOff,
  Mic,
  MicOff,
  Video,
  VideoOff
} from 'lucide-react';
import { useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { RtkMeeting } from '@cloudflare/realtimekit-react-ui';

interface HomeImprovementVideoSessionProps {
  roomId: string;
  participantName: string;
  participantType: 'contractor' | 'customer';
  projectId: string;
  projectDetails?: {
    title: string;
    category: string;
    urgency: string;
    customerName?: string;
    contractorName?: string;
    address?: string;
    budget?: string;
  };
  onEndSession?: () => void;
}

interface ChatMessage {
  id: string;
  sender: 'contractor' | 'customer' | 'system';
  message: string;
  timestamp: Date;
  type: 'text' | 'system' | 'file' | 'estimate';
}

interface EstimateItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
}

// Inner component that uses RealtimeKit hooks (same pattern as Legal template)
const HomeImprovementMeetingComponent: React.FC<{
  authToken: string;
  onEndCall: () => void;
  participantName: string;
  participantType: 'contractor' | 'customer';
  roomId: string;
  projectId: string;
  projectDetails?: any;
}> = ({ authToken, onEndCall, participantName, participantType, roomId, projectId, projectDetails }) => {
  const [meeting, initMeeting] = useRealtimeKitClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showEstimate, setShowEstimate] = useState(false);
  const [showProjectInfo, setShowProjectInfo] = useState(participantType === 'contractor'); // Show by default for contractor
  const [sessionTime, setSessionTime] = useState(0);
  const [newMessage, setNewMessage] = useState('');
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'system',
      message: `Home improvement consultation started for project #${projectId}`,
      timestamp: new Date(),
      type: 'system'
    }
  ]);

  const [estimate, setEstimate] = useState<EstimateItem[]>([
    { description: 'Labor', quantity: 1, unit: 'job', unitPrice: 0, total: 0 },
    { description: 'Materials', quantity: 1, unit: 'job', unitPrice: 0, total: 0 }
  ]);

  const chatEndRef = useRef<HTMLDivElement>(null);

  // Initialize meeting (same pattern as Legal template)
  useEffect(() => {
    const init = async () => {
      try {
        console.log('[HomeImprovementMeetingComponent] Initializing with authToken:', authToken ? 'exists' : 'none');
        console.log('[HomeImprovementMeetingComponent] Room ID:', roomId);
        console.log('[HomeImprovementMeetingComponent] Participant:', participantName, 'Type:', participantType);
        
        await initMeeting({
          authToken: authToken,
          defaults: {
            audio: true,
            video: true,
          },
        });
        
        setIsInitialized(true);
        console.log('[HomeImprovementMeetingComponent] Meeting initialized successfully');
        
      } catch (error) {
        console.error('[HomeImprovementMeetingComponent] Failed to initialize meeting:', error);
        alert(`Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\nPlease refresh the page and try again.`);
      }
    };

    if (authToken && !isInitialized) {
      init();
    }
  }, [authToken, initMeeting, isInitialized]);

  // Timer for session time
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Format time
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Send message
  const sendMessage = () => {
    if (!newMessage.trim()) return;

    const message: ChatMessage = {
      id: Date.now().toString(),
      sender: participantType,
      message: newMessage,
      timestamp: new Date(),
      type: 'text'
    };

    setChatMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  // Add estimate item
  const addEstimateItem = () => {
    setEstimate(prev => [...prev, {
      description: '',
      quantity: 1,
      unit: 'each',
      unitPrice: 0,
      total: 0
    }]);
  };

  // Update estimate item
  const updateEstimateItem = (index: number, field: keyof EstimateItem, value: string | number) => {
    setEstimate(prev => prev.map((item, i) => {
      if (i === index) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          updated.total = updated.quantity * updated.unitPrice;
        }
        return updated;
      }
      return item;
    }));
  };

  // Send estimate
  const sendEstimate = () => {
    const totalEstimate = estimate.reduce((sum, item) => sum + item.total, 0);
    const estimateMessage: ChatMessage = {
      id: Date.now().toString(),
      sender: 'contractor',
      message: `Project Estimate: $${totalEstimate.toLocaleString()}`,
      timestamp: new Date(),
      type: 'estimate'
    };

    setChatMessages(prev => [...prev, estimateMessage]);
    setShowEstimate(false);
  };

  // Handle end call
  const handleEndCall = async () => {
    console.log('[HomeImprovementMeetingComponent] End call requested, performing cleanup...');
    
    if (meeting && isInitialized) {
      try {
        await meeting.leave();
        console.log('[HomeImprovementMeetingComponent] Successfully left video meeting');
      } catch (error) {
        console.warn('[HomeImprovementMeetingComponent] Error leaving meeting:', error);
      }
    }
    
    onEndCall();
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'kitchen': return '🍳';
      case 'bathroom': return '🛁';
      case 'roofing': return '🏠';
      case 'flooring': return '🪵';
      case 'painting': return '🎨';
      case 'electrical': return '⚡';
      case 'plumbing': return '🔧';
      case 'hvac': return '❄️';
      case 'landscaping': return '🌳';
      default: return '🔨';
    }
  };

  if (!meeting || !isInitialized) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-orange-900 via-gray-900 to-yellow-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white mx-auto mb-6" />
          <h1 className="text-white text-3xl font-bold mb-2">Home Improvement Consultation</h1>
          <h2 className="text-orange-200 text-xl mb-4">{participantType === 'contractor' ? 'Contractor' : 'Customer'} Portal</h2>
          <p className="text-gray-300 text-lg">Connecting to consultation...</p>
          <p className="text-gray-400 text-sm mt-4">Room ID: {roomId}</p>
          <p className="text-gray-400 text-sm">Project: #{projectId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      {/* Header Bar */}
      <div className="bg-gray-800 text-white px-6 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="font-semibold">Home Improvement Consultation</h2>
            <p className="text-sm text-gray-300">
              {participantType === 'contractor' 
                ? `Customer: ${projectDetails?.customerName || 'Customer'}` 
                : `Contractor: ${projectDetails?.contractorName || 'Contractor'}`}
            </p>
            <p className="text-xs text-gray-400">Project #{projectId}</p>
          </div>
          {projectDetails && (
            <div className="flex items-center space-x-3 pl-4 border-l border-gray-600">
              <span className="text-lg">{getCategoryIcon(projectDetails.category)}</span>
              <div>
                <p className="text-sm font-medium text-white">
                  {projectDetails.title}
                </p>
                <p className="text-xs text-gray-300">
                  {projectDetails.category} • {projectDetails.urgency}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-6">
          {/* Timer */}
          <div className="flex items-center text-lg font-mono">
            <Clock size={16} className="mr-2" />
            {formatTime(sessionTime)}
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Video Area with RealtimeKit */}
        <div className="flex-1 relative bg-black">
          <div className="h-full w-full relative">
            <RtkMeeting 
              meeting={meeting} 
              showSetupScreen={false}
              mode="fill"
            />
            {/* Overlay to hide any built-in controls at the bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-black opacity-75 pointer-events-none"></div>
          </div>

          {/* Screen Sharing Indicator */}
          {isScreenSharing && (
            <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-lg flex items-center space-x-2">
              <Monitor size={16} />
              <span className="text-sm">Screen Sharing Active</span>
            </div>
          )}

          {/* Controls Overlay - Only show for contractor (host) */}
          {participantType === 'contractor' && (
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
              <div className="bg-gray-800 bg-opacity-90 rounded-full px-4 py-2 flex items-center space-x-3">
              {/* Screen Share (Contractor only) */}
              {participantType === 'contractor' && (
                <button
                  onClick={() => setIsScreenSharing(!isScreenSharing)}
                  className={`p-2 rounded-full transition-colors ${
                    isScreenSharing 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  {isScreenSharing ? <MonitorOff size={18} /> : <Monitor size={18} />}
                </button>
              )}

              {/* Chat */}
              <button
                onClick={() => setShowChat(!showChat)}
                className={`p-2 rounded-full transition-colors ${
                  showChat 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                <MessageSquare size={18} />
              </button>

              {/* Project Info (Contractor only) */}
              {participantType === 'contractor' && (
                <button
                  onClick={() => setShowProjectInfo(!showProjectInfo)}
                  className={`p-2 rounded-full transition-colors ${
                    showProjectInfo 
                      ? 'bg-purple-600 hover:bg-purple-700' 
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Home size={18} />
                </button>
              )}

              {/* Estimate (Contractor only) */}
              {participantType === 'contractor' && (
                <button
                  onClick={() => setShowEstimate(!showEstimate)}
                  className={`p-2 rounded-full transition-colors ${
                    showEstimate 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <FileText size={18} />
                </button>
              )}

              {/* End Call */}
              <button
                onClick={handleEndCall}
                className="p-2 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
              >
                <PhoneOff size={18} />
              </button>
              </div>
            </div>
          )}

          {/* Meeting info overlay */}
          <div className="absolute top-4 left-4 z-50 bg-black bg-opacity-50 text-white p-3 rounded-lg">
            <h3 className="font-semibold">{participantName}</h3>
            <p className="text-sm text-gray-300">Home Improvement Consultation</p>
            <p className="text-xs text-gray-400 mt-1">{participantType} • Connected</p>
          </div>
        </div>

        {/* Sidebar - Chat */}
        {showChat && (
          <div className="w-80 bg-white flex flex-col">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <h3 className="font-semibold text-gray-900">Consultation Chat</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`${
                    msg.sender === 'system' 
                      ? 'text-center' 
                      : msg.sender === participantType 
                        ? 'ml-auto' 
                        : 'mr-auto'
                  }`}
                >
                  {msg.sender === 'system' ? (
                    <div className="bg-blue-50 text-blue-800 text-xs px-3 py-2 rounded-lg">
                      {msg.message}
                    </div>
                  ) : msg.type === 'estimate' ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 max-w-xs">
                      <div className="flex items-center mb-2">
                        <FileText size={16} className="text-green-600 mr-2" />
                        <span className="text-sm font-medium text-green-800">Estimate Shared</span>
                      </div>
                      <p className="text-lg font-bold text-green-900">{msg.message}</p>
                      <p className="text-xs text-green-600 mt-1">
                        {msg.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                        msg.sender === participantType
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-900'
                      }`}
                    >
                      <p>{msg.message}</p>
                      <p className="text-xs opacity-75 mt-1">
                        {msg.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Type a message..."
                />
                <button
                  onClick={sendMessage}
                  className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sidebar - Project Info (Contractor only) */}
        {showProjectInfo && participantType === 'contractor' && (
          <div className="w-80 bg-white flex flex-col">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <h3 className="font-semibold text-gray-900">Project Details</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {projectDetails ? (
                <div className="space-y-6">
                  {/* Project Overview */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start space-x-3 mb-3">
                      <span className="text-2xl">{getCategoryIcon(projectDetails.category)}</span>
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-gray-900 mb-1">
                          {projectDetails.title}
                        </h4>
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                            {projectDetails.category.toUpperCase()}
                          </span>
                          <span className={`text-sm px-2 py-1 rounded-full ${
                            projectDetails.urgency === 'urgent' 
                              ? 'bg-red-100 text-red-800'
                              : projectDetails.urgency === 'moderate'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {projectDetails.urgency.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Customer Information */}
                  <div className="space-y-3">
                    <h5 className="font-semibold text-gray-900 flex items-center">
                      <User size={16} className="mr-2" />
                      Customer Information
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-20">Name:</span>
                        <span className="text-gray-900">{projectDetails.customerName || 'Customer'}</span>
                      </div>
                      {projectDetails.address && (
                        <div className="flex items-start">
                          <MapPin size={14} className="mr-2 mt-0.5 text-gray-500" />
                          <span className="text-gray-900">{projectDetails.address}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-3">
                    <h5 className="font-semibold text-gray-900 flex items-center">
                      <Hammer size={16} className="mr-2" />
                      Project Information
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-20">Project ID:</span>
                        <span className="text-gray-900">#{projectId}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-20">Category:</span>
                        <span className="text-gray-900 capitalize">{projectDetails.category}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-20">Urgency:</span>
                        <span className="text-gray-900 capitalize">{projectDetails.urgency}</span>
                      </div>
                      {projectDetails.budget && (
                        <div className="flex items-center">
                          <DollarSign size={14} className="mr-1 text-gray-500" />
                          <span className="font-medium text-gray-600 w-16">Budget:</span>
                          <span className="text-gray-900">{projectDetails.budget}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Session Information */}
                  <div className="space-y-3">
                    <h5 className="font-semibold text-gray-900 flex items-center">
                      <Calendar size={16} className="mr-2" />
                      Session Information
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-20">Duration:</span>
                        <span className="text-gray-900">{formatTime(sessionTime)}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-20">Room ID:</span>
                        <span className="text-gray-900 text-xs font-mono">{roomId}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Items */}
                  <div className="border-t pt-4">
                    <h5 className="font-semibold text-gray-900 mb-3">Quick Actions</h5>
                    <div className="space-y-2">
                      <button
                        onClick={() => setShowEstimate(!showEstimate)}
                        className="w-full bg-green-50 hover:bg-green-100 text-green-800 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                      >
                        <FileText size={16} className="mr-2" />
                        Create Estimate
                      </button>
                      <button
                        onClick={() => setShowChat(!showChat)}
                        className="w-full bg-blue-50 hover:bg-blue-100 text-blue-800 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                      >
                        <MessageSquare size={16} className="mr-2" />
                        Open Chat
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Home size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No project details available</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sidebar - Estimate (Contractor only) */}
        {showEstimate && participantType === 'contractor' && (
          <div className="w-80 bg-white flex flex-col">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <h3 className="font-semibold text-gray-900">Project Estimate</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {estimate.map((item, index) => (
                  <div key={index} className="border rounded-lg p-3">
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateEstimateItem(index, 'description', e.target.value)}
                      className="w-full mb-2 px-2 py-1 border rounded text-sm"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateEstimateItem(index, 'quantity', Number(e.target.value))}
                        className="px-2 py-1 border rounded text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Unit"
                        value={item.unit}
                        onChange={(e) => updateEstimateItem(index, 'unit', e.target.value)}
                        className="px-2 py-1 border rounded text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Price"
                        value={item.unitPrice}
                        onChange={(e) => updateEstimateItem(index, 'unitPrice', Number(e.target.value))}
                        className="px-2 py-1 border rounded text-sm"
                      />
                    </div>
                    <div className="mt-2 text-right">
                      <span className="font-medium">${item.total.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={addEstimateItem}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-gray-400"
                >
                  + Add Item
                </button>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-semibold">Total:</span>
                    <span className="text-xl font-bold text-green-600">
                      ${estimate.reduce((sum, item) => sum + item.total, 0).toLocaleString()}
                    </span>
                  </div>
                  <button
                    onClick={sendEstimate}
                    className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Share Estimate
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const HomeImprovementVideoSession: React.FC<HomeImprovementVideoSessionProps> = ({
  roomId,
  participantName,
  participantType,
  projectId,
  projectDetails,
  onEndSession
}) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEndedMessage, setShowEndedMessage] = useState(false);

  // Initialize token when component mounts
  useEffect(() => {
    const initializeToken = async () => {
      if (!roomId || !participantName) {
        setError('Missing room ID or participant name');
        setIsInitializing(false);
        return;
      }

      try {
        // Check localStorage for debug info from ContractorDashboard
        const debugInfo = localStorage.getItem('homeimprovement_debug');
        const redirectUrl = localStorage.getItem('homeimprovement_redirect_url');
        
        console.log('🔍 [HomeImprovementVideoSession] Debug info from previous page:', debugInfo ? JSON.parse(debugInfo) : 'None');
        console.log('🔍 [HomeImprovementVideoSession] Expected redirect URL:', redirectUrl);
        console.log('🔍 [HomeImprovementVideoSession] Actual current URL:', window.location.href);
        
        // Debug URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        console.log('🔍 [HomeImprovementVideoSession] URL Parameters:', Object.fromEntries(urlParams.entries()));
        console.log('🔍 [HomeImprovementVideoSession] Component Props:', {
          roomId,
          participantName,
          participantType,
          projectId,
          projectDetails
        });
        
        // Clear localStorage after logging
        localStorage.removeItem('homeimprovement_debug');
        localStorage.removeItem('homeimprovement_redirect_url');
        
        console.log('[HomeImprovementVideoSession] Generating token for consultation:', {
          roomId,
          participantName,
          participantType,
          projectId
        });
        
        // Use debug endpoint (same as Legal template)
        const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/debug/generate-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            meetingId: roomId,
            participantName: participantName,
            isHost: participantType === 'contractor'
          })
        });

        if (response.ok) {
          const tokenData = await response.json();
          setAuthToken(tokenData.token);
          console.log('[HomeImprovementVideoSession] Token generated successfully');
        } else {
          throw new Error(`Failed to generate token: ${response.status}`);
        }
        
        setIsInitializing(false);

      } catch (error) {
        console.error('[HomeImprovementVideoSession] Failed to initialize token:', error);
        setError(error instanceof Error ? error.message : 'Failed to join consultation');
        setIsInitializing(false);
      }
    };

    initializeToken();
  }, [roomId, participantName, participantType, projectId]);

  // Handle end call
  const handleEndCall = async () => {
    try {
      console.log('[HomeImprovementVideoSession] Ending call cleanup...');
      setShowEndedMessage(true);
    } catch (error) {
      console.error('[HomeImprovementVideoSession] Error during call cleanup:', error);
    }
  };

  // Show ended message
  if (showEndedMessage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Consultation Completed</h2>
          <p className="text-gray-600 mb-6">
            {participantType === 'contractor' 
              ? 'Thank you for providing your expertise. Follow up with the customer as needed.'
              : 'Thank you for using our service. Your contractor will follow up with next steps.'}
          </p>
          <button
            onClick={() => onEndSession ? onEndSession() : window.close()}
            className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isInitializing || !authToken) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-orange-900 via-gray-900 to-yellow-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white mx-auto mb-6" />
          <h1 className="text-white text-3xl font-bold mb-2">Home Improvement Consultation</h1>
          <h2 className="text-orange-200 text-xl mb-4">{participantType === 'contractor' ? 'Contractor' : 'Customer'} Portal</h2>
          <p className="text-gray-300 text-lg">Joining consultation...</p>
          <p className="text-gray-400 text-sm mt-4">Room ID: {roomId}</p>
          <p className="text-gray-400 text-sm">Project: #{projectId}</p>
          {error && (
            <div className="mt-4 p-3 bg-red-900/50 border border-red-500 rounded-lg">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-900 via-gray-900 to-red-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-white text-xl mb-2">Consultation Error</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.close()}
            className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Render the RealtimeKit meeting
  return (
    <HomeImprovementMeetingComponent
      authToken={authToken}
      onEndCall={handleEndCall}
      participantName={participantName}
      participantType={participantType}
      roomId={roomId}
      projectId={projectId}
      projectDetails={projectDetails}
    />
  );
};